import type { SheetRegions } from "./types";
import { findIslands } from "./detector";
import { addressFromRC } from "../rules/audit/addressing";
import { classifyRegion, type RegionInput } from "./classify/classifier";
import { loadEffectiveRulesSync } from "./classify/storage";
import { getWorkbookModel, Origin } from "../model";

/**
 * Fast pass: find data islands on every worksheet. Reads occupancy from
 * the substrate's cellOrigin and runs the 4-connected island detector.
 * Empty sheets surface via the workbook-wide sheetsBySheetId catalog
 * so the dialog still lists them.
 *
 * Pair with classifyAllRegions (legacy, its own Excel.run) for the
 * slow classification pass. That stays on Office.js for now because
 * the classifier reconstructs boolean and error cell values, which
 * the substrate's Layer 0 doesn't track separately from cellValueKind.
 */
export async function scanAllWorksheets(): Promise<SheetRegions[]> {
  const model = await getWorkbookModel("workbook");
  const results: SheetRegions[] = [];

  for (const [sheetId, meta] of Array.from(model.catalogs.sheetsBySheetId)) {
    const sheet = model.sheets.get(sheetId);

    if (!sheet) {
      // Empty sheet — no SheetModel built. Emit a zero-island record.
      results.push({
        sheetName: meta.name,
        usedRangeStartRow: 0,
        usedRangeStartCol: 0,
        usedRangeRows: 0,
        usedRangeCols: 0,
        totalNonEmpty: 0,
        islands: [],
      });
      continue;
    }

    const { rows, cols, startRow, startCol } = sheet.bounds;
    const cellOrigin = sheet.cellOrigin;

    // Materialize the occupancy grid the detector expects. Cheap — one
    // bool per cell — and findIslands' shape can't be changed without
    // touching the classifier too.
    const occupied: boolean[][] = [];
    let totalNonEmpty = 0;
    for (let r = 0; r < rows; r++) {
      const rowBase = r * cols;
      const row: boolean[] = new Array(cols);
      for (let cc = 0; cc < cols; cc++) {
        const occ = cellOrigin[rowBase + cc] !== Origin.EMPTY;
        row[cc] = occ;
        if (occ) totalNonEmpty++;
      }
      occupied.push(row);
    }

    const rawIslands = findIslands(occupied);

    results.push({
      sheetName: meta.name,
      usedRangeStartRow: startRow,
      usedRangeStartCol: startCol,
      usedRangeRows: rows,
      usedRangeCols: cols,
      totalNonEmpty,
      islands: rawIslands.map((i) => {
        const absMinRow = startRow + i.minRow;
        const absMinCol = startCol + i.minCol;
        const absMaxRow = startRow + i.maxRow;
        const absMaxCol = startCol + i.maxCol;
        return {
          minRow: absMinRow,
          minCol: absMinCol,
          maxRow: absMaxRow,
          maxCol: absMaxCol,
          cellCount: i.cellCount,
          address:
            absMinRow === absMaxRow && absMinCol === absMaxCol
              ? addressFromRC(absMinRow, absMinCol)
              : `${addressFromRC(absMinRow, absMinCol)}:${addressFromRC(absMaxRow, absMaxCol)}`,
          // classification intentionally omitted — added by classifyAllRegions
        };
      }),
    });
  }

  return results;
}

/**
 * Slow pass: load full cell data (valueTypes + values + formulasR1C1) per
 * sheet, then classify each island's bounding box. Returns a new array with
 * `classification` populated on every island. The input `sheets` array is
 * NOT mutated.
 *
 * Runs in its own Excel.run independently of the fast-pass scanner; callers
 * typically invoke it in the background after opening the dialog so the
 * user sees islands immediately and classifications fill in afterward.
 */
export async function classifyAllRegions(sheets: SheetRegions[]): Promise<SheetRegions[]> {
  const rules = loadEffectiveRulesSync();
  const updated: SheetRegions[] = sheets.map((s) => ({
    ...s,
    islands: s.islands.map((i) => ({ ...i })),
  }));

  await Excel.run(async (context) => {
    const wsCol = context.workbook.worksheets;
    wsCol.load("items/name");
    await context.sync();

    const wsByName = new Map<string, Excel.Worksheet>();
    for (const ws of wsCol.items) wsByName.set(ws.name, ws);

    for (const sheet of updated) {
      if (sheet.islands.length === 0) continue;
      const ws = wsByName.get(sheet.sheetName);
      if (!ws) continue;

      const range = ws.getUsedRangeOrNullObject(true);
      range.load(["valueTypes", "values", "formulasR1C1", "numberFormat"]);
      // eslint-disable-next-line office-addins/no-context-sync-in-loop -- per-sheet load; sheets may have very different sized used ranges
      await context.sync();
      if (range.isNullObject) continue;

      const valueTypes = range.valueTypes;
      const values = range.values;
      const formulasR1C1 = range.formulasR1C1;
      const numberFormat = range.numberFormat;

      for (const island of sheet.islands) {
        const relMinRow = island.minRow - sheet.usedRangeStartRow;
        const relMinCol = island.minCol - sheet.usedRangeStartCol;
        const relMaxRow = island.maxRow - sheet.usedRangeStartRow;
        const relMaxCol = island.maxCol - sheet.usedRangeStartCol;

        const input: RegionInput = {
          minRow: island.minRow,
          minCol: island.minCol,
          maxRow: island.maxRow,
          maxCol: island.maxCol,
          values: sliceGrid(values, relMinRow, relMinCol, relMaxRow, relMaxCol),
          valueTypes: sliceGrid(valueTypes, relMinRow, relMinCol, relMaxRow, relMaxCol),
          formulasR1C1: sliceGrid(formulasR1C1, relMinRow, relMinCol, relMaxRow, relMaxCol),
          numberFormat: sliceGrid(numberFormat, relMinRow, relMinCol, relMaxRow, relMaxCol),
        };
        island.classification = classifyRegion(input, rules);
      }
    }
  });

  return updated;
}

/**
 * Slice a 2D grid to a sub-rectangle. Indices are inclusive and relative to
 * the source grid's own origin (NOT sheet-absolute).
 */
function sliceGrid<T>(
  grid: T[][],
  minRow: number,
  minCol: number,
  maxRow: number,
  maxCol: number
): T[][] {
  const out: T[][] = [];
  for (let r = minRow; r <= maxRow; r++) {
    const src = grid[r] ?? [];
    const row: T[] = [];
    for (let c = minCol; c <= maxCol; c++) {
      row.push(src[c]);
    }
    out.push(row);
  }
  return out;
}
